/**
 * What the curated-text toolbar's buttons do to the text in the box — TROOP-256.
 *
 * **One stored format, two ways in.** The body is Markdown and is stored as Markdown; the toolbar
 * only types the syntax for somebody who has never heard of it, so pasting Markdown works and so
 * does clicking Bold. Each function takes the text and the selection and returns the new text and
 * where the selection should land, so the box can be updated in one step and these can be tested
 * without a browser.
 *
 * **It writes only what the renderer reads** (`curatedText.ts`): `**bold**`, `*italic*`, `- ` and
 * `1. ` lines, and `[words](https://…)`. There is no heading button although the ticket sketched
 * one: the grammar has no headings, so the button would type characters that render as themselves.
 * A section's own title is the heading.
 *
 * Self-contained on purpose: `npm test` runs this under Node's own runner, which resolves imports
 * literally, so it reaches for nothing else in `src/`.
 */

/** The box after a button: its whole text, and the selection to put back. */
export interface TextEdit {
  text: string;
  selectionStart: number;
  selectionEnd: number;
}

/** What an empty selection becomes, selected so the next keystroke replaces it. */
const PLACEHOLDER = { '**': 'bold text', '*': 'italic text' } as const;

/**
 * Bold or italic around the selection — or off again, when the selection already sits just inside
 * that marker.
 *
 * The grammar allows no space just inside a marker and no line break inside one, so the selection
 * is shrunk to its first and last non-space character, and a selection over several lines is
 * wrapped a line at a time. Otherwise "select the word and its trailing space, click Bold" would
 * type `**word **`, which draws as asterisks.
 */
export function toggleEmphasis(
  text: string,
  start: number,
  end: number,
  marker: '**' | '*',
): TextEdit {
  const [from, to] = trimSelection(text, Math.min(start, end), Math.max(start, end));

  // Already wrapped in exactly this marker: take it off. For italic the character beyond must not
  // be another asterisk, or this would peel one star off a bold.
  if (from < to && isWrapped(text, from, to, marker)) {
    const m = marker.length;
    return {
      text: text.slice(0, from - m) + text.slice(from, to) + text.slice(to + m),
      selectionStart: from - m,
      selectionEnd: to - m,
    };
  }

  if (from >= to) {
    const placeholder = PLACEHOLDER[marker];
    return {
      text: text.slice(0, from) + marker + placeholder + marker + text.slice(from),
      selectionStart: from + marker.length,
      selectionEnd: from + marker.length + placeholder.length,
    };
  }

  // A line at a time. Blank lines stay blank, each line's own spaces stay outside its markers, and
  // so does a list marker at the start of a line -- `**- one**` is a paragraph, not a bold item.
  const selected = text.slice(from, to);
  const wrapped = selected
    .split('\n')
    .map((line, i) => {
      // Only a whole line can carry a list marker: the first line may start mid-line.
      const atLineStart = i > 0 || from === 0 || text[from - 1] === '\n';
      const item = atLineStart ? ITEM.exec(line) : null;
      const prefix = item ? item[0] : '';
      const rest = line.slice(prefix.length);
      const body = rest.trim();
      if (!body) return line;
      const lead = rest.slice(0, rest.indexOf(body));
      const trail = rest.slice(lead.length + body.length);
      return `${prefix}${lead}${marker}${body}${marker}${trail}`;
    })
    .join('\n');

  return {
    text: text.slice(0, from) + wrapped + text.slice(to),
    selectionStart: from,
    selectionEnd: from + wrapped.length,
  };
}

/** A list item the renderer reads: up to three spaces, then `-`, `*` or `1.`/`1)`, then a space. */
const ITEM = /^( {0,3})(?:[-*]|\d{1,9}[.)])[ \t]+/;
const BULLET = /^( {0,3})[-*][ \t]+/;
const NUMBER = /^( {0,3})\d{1,9}[.)][ \t]+/;

/**
 * Makes every line the selection touches a bulleted or numbered item — or, when they all already
 * are that kind, plain lines again. Whole lines, because a list item is a line. Blank lines are
 * left alone, and numbering runs 1, 2, 3 over the non-blank ones.
 */
export function toggleList(text: string, start: number, end: number, kind: 'bullet' | 'number'): TextEdit {
  const from = text.lastIndexOf('\n', Math.min(start, end) - 1) + 1;
  const stop = text.indexOf('\n', Math.max(start, end) - (end > start && text[end - 1] === '\n' ? 1 : 0));
  const to = stop === -1 ? text.length : stop;

  const lines = text.slice(from, to).split('\n');
  const filled = lines.filter((line) => line.trim() !== '');
  const same = kind === 'bullet' ? BULLET : NUMBER;
  const removing = filled.length > 0 && filled.every((line) => same.test(line));

  let n = 0;
  const changed = lines
    .map((line) => {
      if (line.trim() === '') return line;
      const bare = line.replace(ITEM, '$1');
      if (removing) return bare;
      n += 1;
      const indent = /^ {0,3}/.exec(bare)?.[0] ?? '';
      return `${indent}${kind === 'bullet' ? '- ' : `${n}. `}${bare.slice(indent.length)}`;
    })
    .join('\n');

  // An empty box gets an item to type into, with the cursor after the marker.
  if (filled.length === 0) {
    const marker = kind === 'bullet' ? '- ' : '1. ';
    return {
      text: text.slice(0, from) + marker + text.slice(from),
      selectionStart: from + marker.length,
      selectionEnd: from + marker.length,
    };
  }

  return {
    text: text.slice(0, from) + changed + text.slice(to),
    selectionStart: from,
    selectionEnd: from + changed.length,
  };
}

/**
 * A link around the selection: `[selection](https://)`, with `https://` selected so pasting the
 * address replaces it. With nothing selected the words are a placeholder to type over first.
 * A line break cannot go inside a link's words, so a selection spanning lines keeps only its first.
 */
export function insertLink(text: string, start: number, end: number): TextEdit {
  const [from, to] = trimSelection(text, Math.min(start, end), Math.max(start, end));
  const selected = text.slice(from, to).split('\n')[0].replace(/[[\]]/g, '');
  const words = selected || 'link words';
  const target = 'https://';
  const inserted = `[${words}](${target})`;
  const after = from + (selected ? text.slice(from, to).split('\n')[0].length : 0);

  const targetAt = from + words.length + 3;

  return selected
    ? {
        text: text.slice(0, from) + inserted + text.slice(after),
        selectionStart: targetAt,
        selectionEnd: targetAt + target.length,
      }
    : {
        text: text.slice(0, from) + inserted + text.slice(from),
        selectionStart: from + 1,
        selectionEnd: from + 1 + words.length,
      };
}

function trimSelection(text: string, start: number, end: number): [number, number] {
  let from = start;
  let to = end;
  while (from < to && /\s/.test(text[from])) from += 1;
  while (to > from && /\s/.test(text[to - 1])) to -= 1;
  return [from, to];
}

function isWrapped(text: string, from: number, to: number, marker: '**' | '*'): boolean {
  const m = marker.length;

  if (text.slice(from - m, from) !== marker || text.slice(to, to + m) !== marker) {
    return false;
  }

  // `*` inside `**word**` is the bold's, not an italic's.
  return marker === '**' || (text[from - m - 1] !== '*' && text[to + m] !== '*');
}
